/**
 * Recording a trade.
 *
 * Three ways in, one way out. Paste the Discord message, drop in a screenshot of it, or fill the
 * form by hand — all three land in the SAME editable review card, and nothing reaches the ledger
 * until somebody presses save. That is the important design decision: the parser and the OCR are
 * both allowed to be wrong, because neither of them is the last word.
 *
 * The review card shows what was understood as chips you can delete, plus a free-text box per
 * side for anything to add. Everything the parser was unsure about is listed above it in plain
 * language rather than swallowed, and the original text stays on screen so the two can be
 * compared without switching windows.
 */

import { useEffect, useRef, useState } from 'react';
import {
  classifyAsset, parseTrade, type ParsedAsset, type ParsedTrade,
} from '../domain/tradeParse.js';
import { currentManagers, managerName } from '../domain/identity.js';
import { toParsedTrade, toStoreTrade } from '../domain/trades.js';
import { GmBadge } from './GmBadge.js';
import { GateTag } from './Gate.js';
import type { GmId, LeagueStore, Trade } from '../store/types.js';

const SEASON = 2026;

type Mode = 'paste' | 'image' | 'form';

const EMPTY: ParsedTrade = {
  sides: [{ gm: null, assets: [] }, { gm: null, assets: [] }],
  date: null,
  season: SEASON,
  warnings: [],
  leftovers: [],
};

export function AddTrade(
  { actor, store, onSaved, editing = null, onCancelEdit, onStrike }: {
    actor: GmId;
    store: LeagueStore;
    onSaved: () => void;
    /**
     * An existing trade being corrected.
     *
     * THE RECORD FORM IS THE EDITOR, deliberately. A separate editing screen is a second place
     * for the two to disagree about what a side may hold, and this form already knows how to say
     * "that is a pick, that is FAAB, that is a clause". Correcting is recording with an id.
     */
    editing?: Trade | null;
    onCancelEdit?: () => void;
    /**
     * Strike the trade being edited. Lives in the editor rather than on the row — Tim, 3
     * September: "correct and strike can be simplified to simply 'edit'" — so the destructive
     * act is one more step past the one that opens the record, not a link beside it on 109 rows.
     */
    onStrike?: () => void;
  },
) {
  // STRIKING ASKS TWICE. Eleven years of this log is the only record most of these deals have.
  const [confirmStrike, setConfirmStrike] = useState(false);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>('paste');
  const [text, setText] = useState('');
  const [draft, setDraft] = useState<ParsedTrade | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [imageName, setImageName] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  // Recording a trade is a thing happening NOW, so the picker is the men who hold a team now.
  // The historical pairing screen is where former managers belong.
  const gms = currentManagers().map((m) => m.id);

  function readText(input: string) {
    setText(input);
    setProblem(null);
    setDraft(input.trim() ? parseTrade(input, SEASON) : null);
  }

  async function readFile(file: File) {
    setProblem(null);
    setImageName(file.name);
    setBusy('Loading the recogniser…');
    try {
      const { readImage } = await import('./ocr.js');
      const out = await readImage(file, (label, pct) => setBusy(`${label}… ${Math.round(pct * 100)}%`));
      setBusy(null);
      readText(out.text);
      if (out.confidence < 70) {
        setProblem(
          `The recogniser was only ${Math.round(out.confidence)}% sure of what it read — check `
          + 'every name below before saving.',
        );
      }
    } catch (e) {
      setBusy(null);
      setProblem(e instanceof Error ? e.message : 'Could not read that image.');
    }
  }

  function edit(next: ParsedTrade) {
    setDraft({ ...next });
  }

  function setSide(i: 0 | 1, patch: Partial<ParsedTrade['sides'][number]>) {
    if (!draft) return;
    const sides = [...draft.sides] as ParsedTrade['sides'];
    sides[i] = { ...sides[i]!, ...patch };
    edit({ ...draft, sides });
  }

  function addAsset(i: 0 | 1, raw: string) {
    if (!draft || !raw.trim()) return;
    const parsed = classifyAsset(raw, draft.season);
    if (!parsed) return;
    setSide(i, { assets: [...draft.sides[i]!.assets, parsed] });
  }

  function dropAsset(i: 0 | 1, at: number) {
    if (!draft) return;
    setSide(i, { assets: draft.sides[i]!.assets.filter((_, n) => n !== at) });
  }

  const ready = Boolean(
    draft && draft.sides[0].gm && draft.sides[1].gm
    && draft.sides[0].gm !== draft.sides[1].gm
    && draft.sides.some((s) => s.assets.length > 0),
  );

  /*
   * A trade handed in for correction opens the form on it, by hand, in `form` mode — there is
   * nothing to paste and nothing to parse, and dropping the reader into the paste box in front
   * of a trade that already exists would invite him to retype it.
   */
  useEffect(() => {
    if (!editing) return;
    setDraft(toParsedTrade(editing));
    setMode('form');
    setText('');
    setImageName(null);
    setProblem(null);
    setOpen(true);
  }, [editing]);

  function close() {
    setDraft(null);
    setText('');
    setImageName(null);
    setConfirmStrike(false);
    setOpen(false);
    onCancelEdit?.();
  }

  async function save() {
    if (!draft || !ready) return;
    await store.saveTrade(toStoreTrade(draft, actor, editing?.id));
    close();
    onSaved();
  }

  if (!open) {
    return (
      <div className="addtradebar">
        <button type="button" className="btn primary" onClick={() => setOpen(true)}>
          Record a trade
        </button>
        <span className="mut">
          Paste the Discord message, drop in a screenshot of it, or fill it in by hand.
        </span>
      </div>
    );
  }

  return (
    <section className="card gated commish addtrade">
      <GateTag level="commish" what="Commissioners — this is the ledger" />
      <div className="striptop">
        <h3 className="striphead">{editing ? 'Correct a trade' : 'Record a trade'}</h3>
        <div className="panes small">
          {(['paste', 'image', 'form'] as Mode[]).map((m) => (
            <button key={m} type="button" className={`pane ${mode === m ? 'on' : ''}`}
              onClick={() => { setMode(m); if (m === 'form' && !draft) setDraft({ ...EMPTY }); }}>
              {m === 'paste' ? 'Paste the message' : m === 'image' ? 'Screenshot' : 'By hand'}
            </button>
          ))}
        </div>
        <button type="button" className="linkbtn" onClick={close}>close</button>
      </div>

      {mode === 'paste' && (
        <>
          <p className="note">
            Copy the declaration straight out of #trade-declarations. Two lines reading
            &ldquo;Rajan gets: …&rdquo; and &ldquo;John gets: …&rdquo; is the shape it reads best,
            but &ldquo;Tim sends X to Luke for Y&rdquo; works too.
          </p>
          <textarea
            className="tradepaste"
            rows={5}
            placeholder={'Rajan gets: Justin Jefferson, Terry McLaurin, 2026 14th, 2026 15th\nJohn gets: Keon Coleman, Jayden Higgins, 2026 9th, 2026 11th'}
            value={text}
            onChange={(e) => readText(e.target.value)}
          />
        </>
      )}

      {mode === 'image' && (
        <>
          <p className="note">
            A screenshot of the Discord message. Dark mode is fine — the image is inverted and
            enlarged before it is read. The recogniser is downloaded the first time you use it, so
            this one step needs a connection; everything else in the app does not.
          </p>
          <div
            className="dropzone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files[0];
              if (f) void readFile(f);
            }}
            onClick={() => fileRef.current?.click()}
            onKeyDown={(e) => { if (e.key === 'Enter') fileRef.current?.click(); }}
            role="button"
            tabIndex={0}
          >
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void readFile(f);
              }}
            />
            {busy ? <b>{busy}</b> : (
              <>
                <b>Drop a screenshot here</b>
                <span className="mut">or tap to choose one · PNG, JPG, HEIC</span>
              </>
            )}
          </div>
          {imageName && !busy && <p className="mut small">Read from {imageName}</p>}
          {text && (
            <details className="rawocr">
              <summary>What the recogniser saw</summary>
              <pre>{text}</pre>
            </details>
          )}
        </>
      )}

      {problem && (
        <p className="chip critical block">
          <span className="ico" aria-hidden="true">▲</span> {problem}
        </p>
      )}

      {draft && (
        <>
          {draft.warnings.length > 0 && (
            <ul className="tight warnlist">
              {draft.warnings.map((w) => <li key={w}>{w}</li>)}
            </ul>
          )}

          <div className="reviewsides">
            {([0, 1] as const).map((i) => (
              <SideEditor
                key={i}
                side={draft.sides[i]!}
                gms={gms}
                onGm={(g) => setSide(i, { gm: g || null })}
                onAdd={(raw) => addAsset(i, raw)}
                onDrop={(at) => dropAsset(i, at)}
              />
            ))}
          </div>

          <div className="formrow trademeta">
            <label>
              Date
              <input type="date" value={draft.date ?? ''}
                onChange={(e) => edit({ ...draft, date: e.target.value || null })} />
            </label>
            <label>
              Draft year the picks belong to
              <input type="number" min={2003} max={2035} value={draft.season}
                onChange={(e) => edit({ ...draft, season: Number(e.target.value) })} />
            </label>
          </div>

          {editing && onStrike && (
            <div className="strikerow">
              {confirmStrike ? (
                <>
                  <span className="mut">Strike it from the log? The row is dropped everywhere it is read; a bundled row can be restored by striking the strike.</span>
                  <button type="button" className="linkbtn tiny rm"
                    onClick={() => { setConfirmStrike(false); onStrike(); close(); }}>
                    yes, strike it
                  </button>
                  <button type="button" className="linkbtn tiny" onClick={() => setConfirmStrike(false)}>keep it</button>
                </>
              ) : (
                <>
                  <span className="mut">Not a correction but a trade that never happened?</span>
                  <button type="button" className="linkbtn tiny rm" onClick={() => setConfirmStrike(true)}>
                    strike this trade
                  </button>
                </>
              )}
            </div>
          )}

          <div className="actions">
            <span className="declstatus">
              {!ready ? 'Name both managers and give at least one side something.'
                : editing ? 'This replaces the trade as it currently reads in the log.'
                  : 'Both sides named — this will be added to the log.'}
            </span>
            <button type="button" className="btn"
              onClick={() => (editing ? close() : (setDraft(null), setText('')))}>
              {editing ? 'Cancel' : 'Discard'}
            </button>
            <button type="button" className="btn primary" disabled={!ready} onClick={() => void save()}>
              {editing ? 'Save the correction' : 'Add to the log'}
            </button>
          </div>
        </>
      )}
    </section>
  );
}

function SideEditor(
  { side, gms, onGm, onAdd, onDrop }: {
    side: { gm: string | null; assets: ParsedAsset[] };
    gms: string[];
    onGm: (gm: string) => void;
    onAdd: (raw: string) => void;
    onDrop: (at: number) => void;
  },
) {
  const [entry, setEntry] = useState('');
  return (
    <div className={`reviewside ${side.gm ? '' : 'unset'}`}>
      <div className="reviewhead">
        {side.gm && <GmBadge gm={side.gm} />}
        <select value={side.gm ?? ''} aria-label="Manager" onChange={(e) => onGm(e.target.value)}>
          <option value="">— who? —</option>
          {gms.map((g) => (
            <option key={g} value={g}>{managerName(g)}</option>
          ))}
        </select>
        <span className="mut">gets</span>
      </div>

      <ul className="assetlist">
        {side.assets.map((a, i) => (
          <li key={`${a.raw}${i}`} className={`asset ${a.kind}`}>
            <span className="tag">{a.kind === 'faab' ? '$' : a.kind}</span>
            <span className="txt">{a.text}</span>
            {a.raw !== a.text && <span className="mut was">was &ldquo;{a.raw}&rdquo;</span>}
            <button type="button" className="linkbtn tiny" aria-label={`Remove ${a.text}`}
              onClick={() => onDrop(i)}>×</button>
          </li>
        ))}
        {side.assets.length === 0 && <li className="mut small">nothing yet</li>}
      </ul>

      <div className="assetadd">
        <input
          value={entry}
          placeholder="Add a player, “2026 9th”, “rights to X” or “5 FAAB”"
          onChange={(e) => setEntry(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onAdd(entry);
              setEntry('');
            }
          }}
        />
        <button type="button" className="btn small"
          onClick={() => { onAdd(entry); setEntry(''); }}>Add</button>
      </div>
    </div>
  );
}
